import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarClock, LayoutGrid, Loader2, Settings2, Users } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { usePoll } from "@/lib/usePoll";
import { useAuth } from "@/context/AuthContext";
import { NoAccess } from "@/components/NoAccess";
import { FloorSetup } from "@/pages/restaurant/FloorSetup";

const TONE = {
  available: "border-emerald-200 bg-emerald-50 text-emerald-900 hover:border-emerald-400",
  occupied: "border-slate-900 bg-slate-900 text-white hover:bg-slate-800",
  reserved: "border-amber-300 bg-amber-50 text-amber-900 hover:border-amber-500",
};
const LABEL = { available: "Available", occupied: "Occupied", reserved: "Reserved" };
const mins = (iso) => Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 60000));

function TableTile({ t, onOpen, busy }) {
  return (
    <button onClick={() => onOpen(t)} disabled={busy} data-testid={`table-${t.name}`} data-status={t.status}
      className={`relative flex min-h-[112px] flex-col justify-between rounded-2xl border-2 p-3 text-left transition-colors duration-150 ${TONE[t.status]}`}>
      <div className="flex items-start justify-between gap-2">
        <span className="font-display text-2xl font-extrabold leading-none">{t.name}</span>
        <span className="flex items-center gap-1 text-xs opacity-70"><Users className="h-3.5 w-3.5" />{t.seats}</span>
      </div>
      <div className="text-xs">
        <div className="font-bold uppercase tracking-wider" data-testid={`table-${t.name}-status`}>{LABEL[t.status]}</div>
        {t.order && <div className="mt-0.5 opacity-80">{t.order.items} items · {mins(t.order.opened_at)} min{t.order.waiter_name ? ` · ${t.order.waiter_name}` : ""}</div>}
        {t.order?.unsent > 0 && <div className="mt-1 inline-block rounded-full bg-amber-400 px-2 py-0.5 text-[10px] font-bold text-slate-950">{t.order.unsent} not sent</div>}
        {t.order?.ready_kots > 0 && <div className="mt-1 ml-1 inline-block rounded-full bg-emerald-400 px-2 py-0.5 text-[10px] font-bold text-slate-950" data-testid={`table-${t.name}-ready`}>Ready</div>}
        {!t.order && t.reservation && <div className="mt-0.5 flex items-center gap-1"><CalendarClock className="h-3.5 w-3.5" />{t.reservation.time} · {t.reservation.name} ({t.reservation.party})</div>}
      </div>
    </button>
  );
}

export default function Tables() {
  const { session } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [setup, setSetup] = useState(false);
  const [denied, setDenied] = useState(false);
  const can = (p) => !!session?.permissions?.[p];
  const load = () => api.get("/restaurant/floor").then((r) => setData(r.data)).catch((e) => e.response?.status === 403 && setDenied(true));
  usePoll(load, 5000, [session?.branch?.id]);
  const open = async (t) => {
    if (t.order) return nav(`/tables/order/${t.order.id}`);
    if (!can("take_orders")) return toast.error("You need the “take orders” permission to open a table");
    if (t.reservation && !window.confirm(`${t.name} is reserved for ${t.reservation.name} at ${t.reservation.time}. Open it anyway?`)) return;
    setBusy(true);
    try {
      const { data: o } = await api.post("/restaurant/orders", { table_id: t.id });
      nav(`/tables/order/${o.id}`);
    } catch (e) { toast.error(errMsg(e)); load(); } finally { setBusy(false); }
  };
  if (denied) return <NoAccess what="tables" />;
  const all = data?.sections.flatMap((s) => s.tables) || [];
  const count = (st) => all.filter((t) => t.status === st).length;
  return (
    <div className="h-full overflow-y-auto" data-testid="tables-page">
      <div className="mx-auto max-w-7xl space-y-5 px-3 py-4 sm:px-6">
        <div className="flex flex-wrap items-center gap-3">
          <LayoutGrid className="h-6 w-6 text-amber-500" /><h1 className="font-display text-2xl font-bold">Tables</h1>
          <div className="flex gap-1.5 text-xs font-semibold" data-testid="tables-legend">
            {["available", "occupied", "reserved"].map((s) => <span key={s} className={`rounded-full border px-2.5 py-1 ${TONE[s]}`} data-testid={`tables-count-${s}`}>{LABEL[s]} {count(s)}</span>)}
          </div>
          {can("tables") && <button onClick={() => setSetup(true)} data-testid="tables-manage" className="ml-auto flex h-11 items-center gap-1.5 rounded-full border border-slate-300 bg-white px-4 text-sm font-bold"><Settings2 className="h-4 w-4" />Sections &amp; tables</button>}
        </div>
        {!data && <div className="grid h-40 place-items-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>}
        {data && !data.sections.length && <p className="rounded-2xl border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500" data-testid="tables-empty">No sections yet. Add a section and tables to start taking orders.</p>}
        {data?.sections.map((s) => (
          <section key={s.id} data-testid={`section-${s.name}`}>
            <h2 className="mb-2 font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-slate-500">{s.name} · {s.tables.length}</h2>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-6">
              {s.tables.map((t) => <TableTile key={t.id} t={t} onOpen={open} busy={busy} />)}
            </div>
          </section>
        ))}
      </div>
      {setup && <FloorSetup onClose={() => { setSetup(false); load(); }} />}
    </div>
  );
}
